import type { User as FirebaseUser } from "firebase/auth";
import { useEffect, useState } from "react";

import Analytics from "js/analytics";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { User } from "js/core/models/user";
import { auth } from "js/firebase/auth";
import { app } from "js/namespaces";

import type { IUserStorageModel } from "common/interfaces";

const logger = getLogger(LogGroup.USER);

interface UseFirebaseUserReturn {
    firebaseUser: FirebaseUser | null;
    loading: boolean;
    error: Error | null;
}

/**
 * Pure authentication hook. Use when you only need auth status or loading state.
 */
export const useFirebaseUser = (): UseFirebaseUserReturn => {
    const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(() => auth().currentUser);
    const [loading, setLoading] = useState(!auth().currentUser);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const unsubscribe = auth().onAuthStateChanged(
            user => {
                setFirebaseUser(user);
                setLoading(false);
                setError(null);
            },
            err => {
                setError(err as Error);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, []);

    return {
        firebaseUser,
        loading,
        error
    };
};

interface UseUserReturn {
    user: IUserStorageModel | null;
    firebaseUser: FirebaseUser | null;
    loading: boolean;
    error: Error | null;
}

/**
 * Full User model with side effects (sets app.user, calls ds.setup(), analytics.identify()).
 * Use when you need model methods: update(), get(), availableTrialDays, etc.
 */
export const useUser = (): UseUserReturn => {
    const { firebaseUser, loading: authLoading } = useFirebaseUser();

    const [userModel, setUserModel] = useState<IUserStorageModel | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (authLoading) {
            return;
        }

        let userInstance: IUserStorageModel | null = null;
        let changeHandler: (() => void) | null = null;

        const loadUserModel = async () => {
            try {
                if (!firebaseUser) {
                    setUserModel(null);
                    app.user = null;
                    setLoading(false);
                    return;
                }

                if (app.user && app.user.id === firebaseUser.uid) {
                    userInstance = app.user;
                } else {
                    userInstance = new User({ id: firebaseUser.uid }, { autoLoad: false });
                    app.user = userInstance;
                    await userInstance.load();
                    await Analytics.util.identify(firebaseUser);
                }

                await ds.setup();

                // Listen to Firebase changes and update React state
                changeHandler = () => setUserModel(userInstance);
                userInstance.on("change", changeHandler);

                setUserModel(userInstance);
                setLoading(false);
                setError(null);
            } catch (err) {
                logger.error(err, "Error loading user model");
                setError(err as Error);
                setLoading(false);
            }
        };

        loadUserModel();

        return () => {
            if (userInstance && changeHandler) {
                userInstance.off("change", changeHandler);
            }
        };
    }, [firebaseUser, authLoading]);

    return {
        user: userModel,
        firebaseUser,
        loading: loading || authLoading,
        error
    };
};
